import { createAudioMeter } from './audioMeter.js';

const html = String.raw;

const TEST_DURATION_MS = 12000;
const SIGNAL_WINDOW_MS = 1400;
const SYSTEM_RMS_THRESHOLD = 0.01;
const MIC_RMS_THRESHOLD = 0.003;

const DEFAULT_COPY = {
  title: 'Audio Setup Wizard',
  subtitle: 'Stelle sicher, dass Systemaudio und Mikrofon sauber ankommen.',
  mobileNotice:
    'Hinweis: Auf Mobilgeräten ist keine Bildschirmfreigabe/Systemaudio möglich. Wir testen nur das Mikrofon.',
  mandatoryNotice:
    'Wenn du den gesamten Bildschirm teilst und Audio aktivierst, wird jedes Geräusch deines Computers aufgenommen (Videocalls, Musik, System- und App-Sounds). Bitte schließe alles, was nicht aufgezeichnet werden soll.',
  consentLabel: 'Ich habe verstanden, dass sämtliches Systemaudio aufgezeichnet wird.',
  steps: [
    { title: 'Vorbereitung', hint: 'Hinweis lesen & bestätigen' },
    { title: 'Bildschirm teilen', hint: 'Gesamter Bildschirm + Audio teilen' },
    { title: 'Audio-Test', hint: 'Kurzer Pegelcheck' },
    { title: 'Bereit', hint: 'Aufnahme starten' },
  ],
  step2: {
    title: 'Bildschirm teilen',
    description:
      'Bitte wähle im Browserdialog folgendes:<br><strong>1. Gesamter Bildschirm</strong><br><strong>2. Audio Teilen</strong>',
    help:
      'Tipp: Wenn du einen Videocall im Browser hast, teile den Bildschirm mit aktivem Audio. Sonst kann Systemaudio fehlen.',
    shareButton: 'Bildschirm teilen',
    statusIdle: 'Noch keine Freigabe gestartet.',
    statusDenied: 'Freigabe abgebrochen oder nicht erlaubt. Bitte erneut versuchen.',
    statusUnsupported: 'Bildschirmfreigabe ist auf Mobilgeräten nicht verfügbar.',
    statusNoAudio: 'Kein Systemaudio erkannt. Bitte „Audio teilen“ aktivieren.',
    statusActive: 'Bildschirmfreigabe aktiv.',
    statusEnded: 'Bildschirmfreigabe beendet. Bitte erneut teilen.',
  },
  step3: {
    title: 'Audio-Test',
    description: 'Spiele kurz einen Ton ab und sprich ins Mikrofon.',
    mobileDescription: 'Sprich kurz ins Mikrofon.',
    countdownLabel: 'Test läuft…',
  },
  step4: {
    title: 'Ergebnis',
    ok: 'Alles bereit. Systemaudio und Mikrofon sind aktiv.',
    micOnly: 'Mikrofon ist bereit. Du kannst starten.',
    systemMissing: 'Systemaudio fehlt. Du kannst trotzdem mit Mikrofon starten.',
    micMissing: 'Mikrofon fehlt. Bitte Berechtigungen prüfen und erneut testen.',
  },
  labels: {
    system: 'Systemaudio',
    mic: 'Mikrofon',
    ok: 'OK',
    missing: 'Fehlt',
    unavailable: 'Nicht verfügbar',
  },
  buttons: {
    next: 'Weiter',
    back: 'Zurück',
    cancel: 'Abbrechen',
    retry: 'Erneut testen',
    start: 'Aufnahme starten',
    startWithoutSystem: 'Trotzdem starten (ohne Systemaudio)',
    startMicOnly: 'Aufnahme starten (nur Mikrofon)',
  },
};

const getAudioTrack = (stream) => (stream ? stream.getAudioTracks()[0] : null);

export const openAudioSetupWizard = (options = {}) =>
  new Promise((resolve) => {
    const copy = { ...DEFAULT_COPY, ...(options.copy ?? {}) };
    const onReady = typeof options.onReady === 'function' ? options.onReady : null;
    const onCancel = typeof options.onCancel === 'function' ? options.onCancel : null;

    const deviceInfo = (() => {
      if (typeof navigator === 'undefined') {
        return { isMobile: false, supportsScreenCapture: false };
      }
      const ua = navigator.userAgent ?? '';
      const uaData = navigator.userAgentData;
      const isMobile = Boolean(uaData?.mobile) || /iphone|ipad|ipod|android/i.test(ua);
      const supportsScreenCapture = Boolean(navigator.mediaDevices?.getDisplayMedia) && !isMobile;
      return { isMobile, supportsScreenCapture };
    })();

    const { isMobile, supportsScreenCapture } = deviceInfo;

    const browserWarning = (() => {
      if (typeof navigator === 'undefined') return '';
      const ua = navigator.userAgent ?? '';
      const isSafari = /safari/i.test(ua) && !/chrome|crios|android|edg/i.test(ua);
      const isFirefox = /firefox/i.test(ua);
      const isChatGptAtlas = /chatgpt|atlas/i.test(ua);
      if (!isSafari && !isFirefox && !isChatGptAtlas) return '';
      return 'Hinweis: Systemaudio funktioniert in Safari, Firefox und ChatGPT Atlas leider nicht. Bitte Chrome oder Edge verwenden.';
    })();

    const mobileWarningMarkup = isMobile
      ? `<div class="audio-setup-callout">${copy.mobileNotice}</div>`
      : '';
    const browserWarningMarkup = browserWarning
      ? `<div class="audio-setup-callout audio-setup-warning">${browserWarning}</div>`
      : '';
    const step3Description = supportsScreenCapture
      ? copy.step3.description
      : (copy.step3.mobileDescription ?? copy.step3.description);
    const micOnlyReadyText = copy.step4.micOnly ?? copy.step4.systemMissing;
    const systemUnavailableLabel = copy.labels?.unavailable ?? 'Nicht verfügbar';
    const startMicOnlyLabel = copy.buttons?.startMicOnly ?? 'Aufnahme starten (nur Mikrofon)';

    const state = {
      step: 1,
      consent: false,
      screenStream: null,
      micStream: null,
      systemMeter: null,
      micMeter: null,
      testTimer: null,
      testEndsAt: 0,
      systemSignal: false,
      micSignal: false,
      systemRms: 0,
      micRms: 0,
      screenEndedHandler: null,
      micEndedHandler: null,
      finished: false,
    };

    const overlay = document.createElement('div');
    overlay.className = 'modal-backdrop audio-setup-backdrop';
    overlay.innerHTML = html`
      <div class="modal-card audio-setup-card" role="dialog" aria-modal="true" aria-label="${copy.title}">
        <header class="audio-setup-header">
          <div>
            <h3>${copy.title}</h3>
            <p class="muted">${copy.subtitle}</p>
          </div>
          <button type="button" class="ghost-button audio-setup-close" data-action="cancel">✕</button>
        </header>
        <div class="audio-setup-steps">
          ${copy.steps
            .map(
              (step, index) => html`
                <div class="audio-setup-step" data-step-pill="${index + 1}">
                  <span class="audio-setup-step-index">${index + 1}</span>
                  <div>
                    <strong>${step.title}</strong>
                    <span class="muted small">${step.hint}</span>
                  </div>
                </div>
              `,
            )
            .join('')}
        </div>
        <div class="audio-setup-body">
          <section class="audio-setup-panel" data-step="1">
            <h4>${copy.steps[0]?.title ?? ''}</h4>
            <p>${copy.mandatoryNotice}</p>
            ${mobileWarningMarkup}
            ${browserWarningMarkup}
            <label class="audio-setup-consent">
              <input type="checkbox" data-consent />
              <span>${copy.consentLabel}</span>
            </label>
            <div class="audio-setup-actions">
              <button type="button" class="button-secondary" data-action="cancel">${copy.buttons.cancel}</button>
              <button type="button" class="button" data-action="next" disabled>${copy.buttons.next}</button>
            </div>
          </section>
          <section class="audio-setup-panel" data-step="2">
            <h4>${copy.step2.title}</h4>
            <p>${copy.step2.description}</p>
            <div class="audio-setup-callout">${copy.step2.help}</div>
            <p class="audio-setup-status muted" data-screen-status>${copy.step2.statusIdle}</p>
            <div class="audio-setup-actions">
              <button type="button" class="button-secondary" data-action="back">${copy.buttons.back}</button>
              <button type="button" class="button" data-action="share">${copy.step2.shareButton}</button>
              <button type="button" class="button-secondary" data-action="cancel">${copy.buttons.cancel}</button>
            </div>
          </section>
          <section class="audio-setup-panel" data-step="3">
            <h4>${copy.step3.title}</h4>
            <p>${step3Description}</p>
            <div class="audio-setup-meter-grid">
              <div class="audio-setup-meter-card" data-meter="system">
                <div class="audio-setup-meter-header">
                  <strong>${copy.labels.system}</strong>
                  <span class="audio-setup-meter-status" data-meter-status="system">${copy.labels.missing}</span>
                </div>
                <div class="audio-setup-meter">
                  <div class="audio-setup-meter-fill" data-meter-fill="system"></div>
                </div>
              </div>
              <div class="audio-setup-meter-card" data-meter="mic">
                <div class="audio-setup-meter-header">
                  <strong>${copy.labels.mic}</strong>
                  <span class="audio-setup-meter-status" data-meter-status="mic">${copy.labels.missing}</span>
                </div>
                <div class="audio-setup-meter">
                  <div class="audio-setup-meter-fill" data-meter-fill="mic"></div>
                </div>
              </div>
            </div>
            <p class="audio-setup-countdown muted" data-test-countdown>${copy.step3.countdownLabel}</p>
            <div class="audio-setup-actions">
              <button type="button" class="button-secondary" data-action="back">${copy.buttons.back}</button>
              <button type="button" class="button-secondary" data-action="cancel">${copy.buttons.cancel}</button>
            </div>
          </section>
          <section class="audio-setup-panel" data-step="4">
            <h4>${copy.step4.title}</h4>
            <div class="audio-setup-result" data-result-chip></div>
            <p class="audio-setup-result-text" data-result-text></p>
            <div class="audio-setup-result-grid">
              <div class="audio-setup-result-item" data-result-system>
                <span>${copy.labels.system}</span>
                <strong></strong>
              </div>
              <div class="audio-setup-result-item" data-result-mic>
                <span>${copy.labels.mic}</span>
                <strong></strong>
              </div>
            </div>
            <div class="audio-setup-actions">
              <button type="button" class="button-secondary" data-action="retry">${copy.buttons.retry}</button>
              <button type="button" class="button-secondary" data-action="start-without-system">${copy.buttons.startWithoutSystem}</button>
              <button type="button" class="button" data-action="start">${copy.buttons.start}</button>
              <button type="button" class="button-secondary" data-action="cancel">${copy.buttons.cancel}</button>
            </div>
          </section>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);

    const stepPanels = Array.from(overlay.querySelectorAll('[data-step]'));
    const stepPills = Array.from(overlay.querySelectorAll('[data-step-pill]'));
    const consentCheckbox = overlay.querySelector('[data-consent]');
    const nextButton = overlay.querySelector('[data-action="next"]');
    const backButtons = Array.from(overlay.querySelectorAll('[data-action="back"]'));
    const cancelButtons = Array.from(overlay.querySelectorAll('[data-action="cancel"]'));
    const shareButton = overlay.querySelector('[data-action="share"]');
    const retryButton = overlay.querySelector('[data-action="retry"]');
    const startButton = overlay.querySelector('[data-action="start"]');
    const startWithoutSystemButton = overlay.querySelector('[data-action="start-without-system"]');
    const screenStatusEl = overlay.querySelector('[data-screen-status]');
    const countdownEl = overlay.querySelector('[data-test-countdown]');
    const resultChipEl = overlay.querySelector('[data-result-chip]');
    const resultTextEl = overlay.querySelector('[data-result-text]');
    const resultSystemEl = overlay.querySelector('[data-result-system] strong');
    const resultMicEl = overlay.querySelector('[data-result-mic] strong');
    const systemMeterFill = overlay.querySelector('[data-meter-fill="system"]');
    const micMeterFill = overlay.querySelector('[data-meter-fill="mic"]');
    const systemStatusLabel = overlay.querySelector('[data-meter-status="system"]');
    const micStatusLabel = overlay.querySelector('[data-meter-status="mic"]');

    if (!supportsScreenCapture) {
      if (startButton) {
        startButton.style.display = 'none';
      }
      if (startWithoutSystemButton) {
        startWithoutSystemButton.textContent = startMicOnlyLabel;
        startWithoutSystemButton.classList.remove('button-secondary');
        startWithoutSystemButton.classList.add('button');
      }
    }

    const applyStepState = () => {
      stepPanels.forEach((panel) => {
        const step = Number(panel.dataset.step);
        panel.classList.toggle('active', step === state.step);
      });
      stepPills.forEach((pill) => {
        const step = Number(pill.dataset.stepPill);
        pill.classList.toggle('active', step === state.step);
        pill.classList.toggle('complete', step < state.step);
      });
    };

    const updateConsentState = () => {
      if (!nextButton) return;
      nextButton.disabled = !state.consent;
    };

    const setScreenStatus = (message, tone = 'muted') => {
      if (!screenStatusEl) return;
      screenStatusEl.textContent = message;
      screenStatusEl.dataset.tone = tone;
    };

    const cleanupMeters = async () => {
      if (state.systemMeter) {
        await state.systemMeter.stop();
        state.systemMeter = null;
      }
      if (state.micMeter) {
        await state.micMeter.stop();
        state.micMeter = null;
      }
    };

    const stopTest = async () => {
      if (state.testTimer) {
        clearInterval(state.testTimer);
        state.testTimer = null;
      }
      state.testEndsAt = 0;
      if (countdownEl) {
        countdownEl.textContent = '';
      }
      await cleanupMeters();
    };

    const stopStreams = () => {
      [state.screenStream, state.micStream].forEach((stream) => {
        if (!stream) return;
        stream.getTracks().forEach((track) => {
          try {
            track.stop();
          } catch (error) {
            console.warn('Failed to stop track', error);
          }
        });
      });
      state.screenStream = null;
      state.micStream = null;
    };

    const detachTrackHandlers = () => {
      const screenTrack = getAudioTrack(state.screenStream) || state.screenStream?.getVideoTracks()[0];
      if (screenTrack && state.screenEndedHandler) {
        screenTrack.removeEventListener('ended', state.screenEndedHandler);
      }
      const micTrack = getAudioTrack(state.micStream);
      if (micTrack && state.micEndedHandler) {
        micTrack.removeEventListener('ended', state.micEndedHandler);
      }
      state.screenEndedHandler = null;
      state.micEndedHandler = null;
    };

    const finish = async (payload, { keepStreams = false } = {}) => {
      if (state.finished) return;
      state.finished = true;
      detachTrackHandlers();
      await stopTest();
      if (!keepStreams) {
        stopStreams();
      }
      overlay.remove();
      document.removeEventListener('keydown', handleKeydown);
      resolve(payload);
    };

    const handleCancel = () => {
      if (onCancel) {
        onCancel();
      }
      finish(null, { keepStreams: false });
    };

    const handleReady = (payload) => {
      if (onReady) {
        onReady(payload);
      }
      finish(payload, { keepStreams: true });
    };

    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        handleCancel();
      }
    };

    document.addEventListener('keydown', handleKeydown);

    cancelButtons.forEach((button) => {
      button.addEventListener('click', handleCancel);
    });

    backButtons.forEach((button) => {
      button.addEventListener('click', async () => {
        if (state.step === 2) {
          state.step = 1;
        } else if (state.step === 3) {
          await stopTest();
          state.step = supportsScreenCapture ? 2 : 1;
        }
        applyStepState();
      });
    });

    const updateMeter = (fillEl, statusEl, rms, hasSignal, color) => {
      if (fillEl) {
        const normalized = Math.max(0, Math.min(1, rms * 6));
        fillEl.style.width = `${Math.round(normalized * 100)}%`;
        fillEl.style.background = color;
      }
      if (statusEl) {
        statusEl.textContent = hasSignal ? copy.labels.ok : copy.labels.missing;
        statusEl.dataset.state = hasSignal ? 'ok' : 'missing';
      }
    };

    const markSystemUnavailable = () => {
      if (systemStatusLabel) {
        systemStatusLabel.textContent = systemUnavailableLabel;
        systemStatusLabel.dataset.state = 'unavailable';
      }
      if (systemMeterFill) {
        systemMeterFill.style.width = '0%';
        systemMeterFill.style.background = '#94a3b8';
      }
    };

    const updateResultState = () => {
      const hasMic = state.micSignal;
      const systemAvailable = supportsScreenCapture;
      const hasSystem = systemAvailable ? state.systemSignal : false;
      if (resultSystemEl) {
        if (systemAvailable) {
          resultSystemEl.textContent = hasSystem ? copy.labels.ok : copy.labels.missing;
          resultSystemEl.dataset.state = hasSystem ? 'ok' : 'missing';
        } else {
          resultSystemEl.textContent = systemUnavailableLabel;
          resultSystemEl.dataset.state = 'unavailable';
        }
      }
      if (resultMicEl) {
        resultMicEl.textContent = hasMic ? copy.labels.ok : copy.labels.missing;
        resultMicEl.dataset.state = hasMic ? 'ok' : 'missing';
      }

      if (resultChipEl) {
        let status = 'error';
        if (!systemAvailable) {
          if (hasMic) {
            status = 'success';
            resultChipEl.textContent = micOnlyReadyText;
          } else {
            status = 'error';
            resultChipEl.textContent = copy.step4.micMissing;
          }
        } else if (hasMic && hasSystem) {
          status = 'success';
          resultChipEl.textContent = copy.step4.ok;
        } else if (hasMic && !hasSystem) {
          status = 'warn';
          resultChipEl.textContent = copy.step4.systemMissing;
        } else {
          status = 'error';
          resultChipEl.textContent = copy.step4.micMissing;
        }
        resultChipEl.dataset.state = status;
      }
      if (resultTextEl) {
        if (!systemAvailable) {
          resultTextEl.textContent = hasMic ? micOnlyReadyText : copy.step4.micMissing;
        } else if (hasMic && hasSystem) {
          resultTextEl.textContent = copy.step4.ok;
        } else if (hasMic && !hasSystem) {
          resultTextEl.textContent = copy.step4.systemMissing;
        } else {
          resultTextEl.textContent = copy.step4.micMissing;
        }
      }

      if (startButton) {
        startButton.disabled = systemAvailable ? !(hasMic && hasSystem) : true;
      }
      if (startWithoutSystemButton) {
        startWithoutSystemButton.disabled = systemAvailable ? !(hasMic && !hasSystem) : !hasMic;
      }
    };

    const completeTest = async () => {
      await stopTest();
      state.step = 4;
      applyStepState();
      updateResultState();
    };

    const ensureMicStream = async () => {
      const currentTrack = getAudioTrack(state.micStream);
      const trackUsable = currentTrack
        && currentTrack.readyState !== 'ended'
        && currentTrack.enabled !== false
        && currentTrack.muted !== true;
      if (trackUsable) {
        return;
      }
      try {
        state.micStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
          },
        });
        attachStreamHandlers();
      } catch (error) {
        console.warn('Microphone permission failed', error);
        state.micStream = null;
      }
    };

    const startAudioTest = async () => {
      await stopTest();
      state.systemSignal = false;
      state.micSignal = false;
      state.systemRms = 0;
      state.micRms = 0;

      await ensureMicStream();

      if (!supportsScreenCapture) {
        markSystemUnavailable();
      } else if (state.screenStream) {
        state.systemMeter = createAudioMeter({
          stream: state.screenStream,
          threshold: SYSTEM_RMS_THRESHOLD,
          windowMs: SIGNAL_WINDOW_MS,
          onUpdate: ({ rms, hasSignal }) => {
            state.systemRms = rms;
            state.systemSignal = hasSignal;
            updateMeter(systemMeterFill, systemStatusLabel, rms, hasSignal, '#60a5fa');
          },
        });
        await state.systemMeter.start();
      }

      if (state.micStream) {
        state.micMeter = createAudioMeter({
          stream: state.micStream,
          threshold: MIC_RMS_THRESHOLD,
          windowMs: SIGNAL_WINDOW_MS,
          onUpdate: ({ rms, hasSignal }) => {
            state.micRms = rms;
            state.micSignal = hasSignal;
            updateMeter(micMeterFill, micStatusLabel, rms, hasSignal, '#f97316');
          },
        });
        await state.micMeter.start();
      }

      state.testEndsAt = Date.now() + TEST_DURATION_MS;
      state.testTimer = setInterval(() => {
        const remaining = Math.max(0, state.testEndsAt - Date.now());
        const seconds = Math.ceil(remaining / 1000);
        if (countdownEl) {
          countdownEl.textContent = `${copy.step3.countdownLabel} ${seconds}s`;
        }
        if (remaining <= 0) {
          completeTest();
        }
      }, 250);
    };

    const attachStreamHandlers = () => {
      detachTrackHandlers();
      const screenTrack = getAudioTrack(state.screenStream) || state.screenStream?.getVideoTracks()[0];
      if (screenTrack) {
        state.screenEndedHandler = () => {
          setScreenStatus(copy.step2.statusEnded, 'error');
          stopStreams();
          state.step = 2;
          applyStepState();
        };
        screenTrack.addEventListener('ended', state.screenEndedHandler);
      }
      const micTrack = getAudioTrack(state.micStream);
      if (micTrack) {
        state.micEndedHandler = () => {
          stopStreams();
          state.step = supportsScreenCapture ? 2 : 1;
          if (supportsScreenCapture) {
            setScreenStatus(copy.step2.statusEnded, 'error');
          }
          applyStepState();
        };
        micTrack.addEventListener('ended', state.micEndedHandler);
      }
    };

    const acquireStreams = async () => {
      setScreenStatus(copy.step2.statusIdle, 'muted');
      if (!supportsScreenCapture) {
        setScreenStatus(copy.step2.statusUnsupported, 'error');
        return false;
      }
      if (!navigator?.mediaDevices?.getDisplayMedia) {
        setScreenStatus(copy.step2.statusDenied, 'error');
        return false;
      }
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: true,
        });
        if (!screenStream.getAudioTracks().length) {
          screenStream.getTracks().forEach((track) => track.stop());
          setScreenStatus(copy.step2.statusNoAudio, 'error');
          return false;
        }
        state.screenStream = screenStream;
        setScreenStatus(copy.step2.statusActive, 'success');

        try {
          state.micStream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });
        } catch (error) {
          console.warn('Microphone permission failed', error);
          state.micStream = null;
        }

        attachStreamHandlers();
        return true;
      } catch (error) {
        console.warn('Screen capture failed', error);
        setScreenStatus(copy.step2.statusDenied, 'error');
        return false;
      }
    };

    if (consentCheckbox) {
      consentCheckbox.addEventListener('change', (event) => {
        state.consent = Boolean(event.target.checked);
        updateConsentState();
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', async () => {
        if (!state.consent) return;
        state.step = supportsScreenCapture ? 2 : 3;
        applyStepState();
        if (!supportsScreenCapture) {
          await startAudioTest();
        }
      });
    }

    if (shareButton) {
      shareButton.addEventListener('click', async () => {
        if (!supportsScreenCapture) {
          setScreenStatus(copy.step2.statusUnsupported, 'error');
          return;
        }
        shareButton.disabled = true;
        const ok = await acquireStreams();
        shareButton.disabled = false;
        if (!ok) return;
        state.step = 3;
        applyStepState();
        await startAudioTest();
      });
    }

    if (retryButton) {
      retryButton.addEventListener('click', async () => {
        state.step = 3;
        applyStepState();
        await startAudioTest();
      });
    }

    if (startButton) {
      startButton.addEventListener('click', () => {
        if (!(state.micSignal && state.systemSignal)) return;
        handleReady({
          screenStream: state.screenStream,
          micStream: state.micStream,
          status: {
            systemAudioDetected: state.systemSignal,
            micDetected: state.micSignal,
            selection: 'full',
          },
        });
      });
    }

    if (startWithoutSystemButton) {
      startWithoutSystemButton.addEventListener('click', () => {
        if (!(state.micSignal && !state.systemSignal)) return;
        if (state.screenStream) {
          state.screenStream.getTracks().forEach((track) => track.stop());
          state.screenStream = null;
        }
        handleReady({
          screenStream: null,
          micStream: state.micStream,
          status: {
            systemAudioDetected: false,
            micDetected: state.micSignal,
            selection: 'mic-only',
          },
        });
      });
    }

    applyStepState();
    updateConsentState();
  });
